import { CapacityIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { isEquipmentBased } from "common";
import React, { useEffect, useState } from "react";
import { Event, Experience, OPEN_UNLIMITED, Permission, Timeslot } from "types";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useVisibility } from "../../../../hooks/useVisibility";
import {
    getHoldStatusCount,
    getMaxCapacity,
    getOpenCapacity,
    hasWaitlists,
    isBlackedOut,
} from "../../../../models/Timeslot";
import { IsGranted } from "../../../Common/IsGranted";
import { NoTranslate } from "../../../Common/NoTranslate";
import { EditEventCapacityModal } from "../../../Event/EditEventCapacityModal";
import { ManageActionButton } from "../ManageActionButton";
import { RosterList, RosterListItem } from "../RosterList";

interface EventCapacityProps {
    timeslot: Timeslot;
    editable?: boolean;
    event?: Event;
    experience?: Experience;
    handleDashboardRoster: (eventId: ID, shouldRedirectToDashboard?: boolean) => void;
}

export const EventCapacity = ({
    timeslot,
    editable = true,
    event,
    experience,
    handleDashboardRoster,
}: EventCapacityProps) => {
    const isEquipment = experience ? isEquipmentBased(experience) : false;
    // For equipment experiences, reserved = total equipment units booked (sum of resourceUsages counts)
    const equipmentReserved = isEquipment
        ? (event?.resourceUsages ?? []).reduce((sum, r) => sum + (r.count ?? 0), 0)
        : null;
    const [isOpenEditEventCapacityModal, { show: showEditEventCapacityModal, hide: hideEditEventCapacityModal }] =
        useVisibility(false);
    const invalidate = useInvalidate();
    // The capacity helpers report units for equipment slots; event-level max/open are guest values,
    // so they only override for non-equipment experiences.
    const [{ max: guestMax, open: guestOpen }, setMaxOpen] = useState({
        max: event?.max ?? getMaxCapacity(timeslot),
        open: event?.open ?? getOpenCapacity(timeslot),
    });
    useEffect(() => {
        setMaxOpen((previousState) => ({
            max: event?.max ?? previousState.max,
            open: event?.open ?? previousState.open,
        }));
    }, [event, event?.max, event?.open]);
    useEffect(() => {
        setMaxOpen({
            max: getMaxCapacity(timeslot),
            open: getOpenCapacity(timeslot),
        });
    }, [timeslot, timeslot?.capacity?.max, timeslot?.capacity?.open]);
    const reserved = equipmentReserved ?? timeslot.capacity.reserved;
    const configuredMax = isEquipment ? getMaxCapacity(timeslot) : guestMax;
    const configuredOpen = isEquipment ? getOpenCapacity(timeslot) : guestOpen;
    const isSlotBlackedOut = isBlackedOut(timeslot);
    const max = isSlotBlackedOut ? reserved : configuredMax;
    const open = isSlotBlackedOut ? getOpenCapacity(timeslot) : configuredOpen;
    const holdStatusCount = getHoldStatusCount(timeslot.statusCounts) ?? 0;
    const handleCreatedEvent = (eventId: ID, shouldRedirectToDashboard = false) => {
        invalidate("events");
        invalidate("timeslots");

        handleDashboardRoster(eventId, shouldRedirectToDashboard);
    };

    const unavailabilityMessage = timeslot.unavailability?.message;
    const availableCount = (
        <span className={clsx(open < 0 && "text-danger")}>
            <var data-var="availability">{open}</var> Available
        </span>
    );

    return (
        <div>
            <RosterList
                heading="Capacity"
                count={open}
                total={max}
                showCountIfZero={false}
                icon={<CapacityIcon />}
                className={clsx("mb-2", !isEquipment && "hover:cursor-pointer")}
                onClick={isEquipment ? undefined : showEditEventCapacityModal}
            >
                {unavailabilityMessage ? (
                    <Tooltip
                        content={<NoTranslate className="whitespace-pre-line">{unavailabilityMessage}</NoTranslate>}
                    >
                        <li className="break-all">
                            <span className="text-gray-darker line-clamp-1">{availableCount}</span>
                        </li>
                    </Tooltip>
                ) : (
                    max < OPEN_UNLIMITED && <RosterListItem>{availableCount}</RosterListItem>
                )}
                <RosterListItem>
                    <var data-var="reservedCount">{reserved}</var> Reserved
                </RosterListItem>
                {holdStatusCount ? (
                    <RosterListItem className="text-caution">
                        <var data-var="holdStatusCount">{holdStatusCount}</var> Purchasing
                    </RosterListItem>
                ) : null}

                <IsGranted permissions={Permission.WAITLISTS_VIEW}>
                    {hasWaitlists(timeslot) ? (
                        <RosterListItem className="text-caution">
                            <var data-var="waitlistCount">{timeslot.waitlistCount}</var> Waitlisted
                        </RosterListItem>
                    ) : null}
                </IsGranted>
            </RosterList>
            {editable && !isEquipment && (
                <IsGranted permissions={Permission.MODIFY_EQUIPMENT_CAPACITY}>
                    <ManageActionButton text="Manage Capacity" onClick={showEditEventCapacityModal} />
                    <EditEventCapacityModal
                        timeslot={timeslot}
                        isOpen={isOpenEditEventCapacityModal}
                        onCreatedEvent={handleCreatedEvent}
                        onClose={hideEditEventCapacityModal}
                    />
                </IsGranted>
            )}
        </div>
    );
};
